
import { motion } from "framer-motion";
import { slideInVariants, fadeInVariants } from "@/lib/animations";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { HeartPulse, Building } from "lucide-react";

const IntroCards = () => {
  return (
    <motion.div
      initial="initial"
      whileInView="animate"
      viewport={{ once: true }}
      variants={fadeInVariants}
    >
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Decorative elements with #99B7AF */}
        <motion.div
          className="absolute top-0 left-1/4 w-24 h-24 rounded-full opacity-10 blur-xl"
          style={{ backgroundColor: '#99B7AF' }}
          animate={{
            y: [0, -20, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-16 h-16 rounded-full opacity-15"
          style={{ backgroundColor: '#99B7AF' }}
          animate={{
            rotate: [0, 360],
            scale: [1, 0.8, 1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
          <motion.div
            variants={slideInVariants.left}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border-2 border-transparent hover:border-opacity-50 p-4 sm:p-6 flex flex-col hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            style={{ 
              borderColor: '#99B7AF',
              boxShadow: '0 10px 25px rgba(153, 183, 175, 0.1)'
            }}
          >
            {/* Decorative corner element */}
            <motion.div
              className="absolute top-0 right-0 w-20 h-20 opacity-5"
              style={{ backgroundColor: '#99B7AF' }}
              animate={{
                rotate: [0, 90, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            
            <div className="flex items-center gap-3 mb-3 sm:mb-4 relative z-10">
              <div className="p-2 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg relative">
                <HeartPulse className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
                {/* Pulse indicator */}
                <motion.div
                  className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
                  style={{ backgroundColor: '#99B7AF' }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-brand-primary">Para Você</h3>
            </div>
            <p className="text-gray-700 mb-4 sm:mb-6 flex-grow text-sm sm:text-base">
              Cuide da sua saúde mental e bem-estar emocional com ferramentas personalizadas para seu crescimento pessoal.
            </p>
            <Link to="/signup" aria-label="Registre-se para começar a cuidar da sua saúde mental">
              <Button className="w-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:from-brand-secondary hover:to-brand-primary transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 opacity-0 hover:opacity-20"
                  style={{ backgroundColor: '#99B7AF' }}
                  whileHover={{ opacity: 0.2 }}
                  transition={{ duration: 0.3 }}
                />
                <span className="relative z-10">Começar Agora</span>
              </Button>
            </Link>
          </motion.div>
          
          <motion.div
            variants={slideInVariants.right}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border-2 border-transparent hover:border-opacity-50 p-4 sm:p-6 flex flex-col hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            style={{ 
              borderColor: '#99B7AF',
              boxShadow: '0 10px 25px rgba(153, 183, 175, 0.1)'
            }}
          >
            <div className="flex items-center gap-3 mb-3 sm:mb-4 relative z-10">
              <div className="p-2 bg-gradient-to-br from-brand-secondary to-brand-accent rounded-lg relative">
                <Building className="w-5 h-5 sm:w-6 sm:h-6 text-brand-primary" aria-hidden="true" />
                {/* Status indicator */}
                <motion.div
                  className="absolute -bottom-1 -right-1 w-2 h-2 rounded-full"
                  style={{ backgroundColor: '#99B7AF' }}
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-brand-primary">Para Empresas</h3>
            </div>
            <p className="text-gray-700 mb-4 sm:mb-6 flex-grow text-sm sm:text-base">
              Potencialize o bem-estar da sua equipe e aumente a produtividade com nossa solução corporativa completa.
            </p>
            <Link to="/corporativo" aria-label="Conheça nossas soluções corporativas para saúde mental">
              <Button className="w-full bg-gradient-to-r from-brand-secondary to-brand-accent text-brand-primary hover:from-brand-primary hover:to-brand-secondary hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg relative overflow-hidden border-2 border-transparent hover:border-opacity-30"
                      style={{ borderColor: '#99B7AF' }}>
                <motion.div
                  className="absolute inset-0 opacity-0 hover:opacity-15"
                  style={{ backgroundColor: '#99B7AF' }}
                  whileHover={{ opacity: 0.15 }}
                  transition={{ duration: 0.3 }}
                />
                <span className="relative z-10">Conhecer Soluções</span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default IntroCards;
